import React, { useState } from 'react';
import {
  Badge, Box, Button, Flex, Input, InputGroup, InputLeftElement, MenuGroup, MenuItem,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalOverlay,
  Text, useColorModeValue, useDisclosure,
} from '@chakra-ui/react';
import { FiSearch, FiUserCheck } from 'react-icons/fi';
import useCurrentUser from '../hooks/useCurrentUser';
import useUsers from '../hooks/useUsers';
import {
  startUserImpersonation, getImpersonatedRoleName, getImpersonatedUserId,
} from '../utils/impersonation';
import { AccessRole, ACCESS_ROLE_LABELS } from '../models/user';

// item 851 — SUPER-ADMIN testing tool: impersonate a specific USER. The whole app then runs AS that
// user — their real role AND bindings (a Customer Builder's bound customer, an Outside Sales rep's
// book), because the backend maps the effective user id to the target (super-admin verified).
// item 1065 (Chad: "the list is so large I cannot see it all and it is all over the place") —
// the flat 100-row menu dump is now ONE menu item opening a searchable picker: users grouped
// by role, alphabetized, scrollable — and EVERY non-archived user appears automatically
// (the old isActive filter silently hid invited-but-not-yet-accepted users, so new seats
// never showed up; they now list with an INVITED badge).
const ROLE_ORDER: (AccessRole | undefined)[] = [
  AccessRole.Admin, AccessRole.Accountant, AccessRole.Executive, AccessRole.Team,
  AccessRole.OutsideSales, AccessRole.CustomerBuilder, undefined,
];

const ImpersonateUserGroup: React.FC = () => {
  const { isSuperAdmin, user } = useCurrentUser();
  const { data } = useUsers(isSuperAdmin);
  const modal = useDisclosure();
  const [q, setQ] = useState('');
  const rowHover = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const subColor = useColorModeValue('gray.500', 'whiteAlpha.600');

  if (!isSuperAdmin || getImpersonatedRoleName() || getImpersonatedUserId()) return null;

  const term = q.trim().toLowerCase();
  const users = (data?.index ?? [])
    .filter((u) => u.id !== user?.id && !u.isArchive)
    .filter((u) => !term
      || (u.name ?? '').toLowerCase().includes(term)
      || String((u as any).email ?? '').toLowerCase().includes(term)
      || (u.accessRole !== undefined && ACCESS_ROLE_LABELS[u.accessRole].toLowerCase().includes(term)))
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  if ((data?.index ?? []).length === 0) return null;

  const groups = ROLE_ORDER
    .map((role) => ({
      label: role !== undefined ? ACCESS_ROLE_LABELS[role] : 'No role set',
      members: users.filter((u) => u.accessRole === role),
    }))
    .filter((g) => g.members.length > 0);

  return (
    <>
      <MenuGroup title="Super-admin">
        <MenuItem icon={<FiUserCheck />} onClick={modal.onOpen}>
          Impersonate user…
        </MenuItem>
      </MenuGroup>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Impersonate a user</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={5}>
            <Text fontSize="xs" color={subColor} mb={3}>
              The whole app runs exactly as that person sees it — their role, bindings, and
              portal. Exit any time from the preview banner.
            </Text>
            <InputGroup size="sm" mb={3}>
              <InputLeftElement pointerEvents="none"><FiSearch opacity={0.5} /></InputLeftElement>
              <Input
                placeholder="Search by name, email, or role…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
            </InputGroup>
            {groups.length === 0 && (
              <Text fontSize="sm" color={subColor}>No users match.</Text>
            )}
            {groups.map((g) => (
              <Box key={g.label} mb={3}>
                <Text fontSize="0.65rem" fontWeight="bold" letterSpacing="0.08em" textTransform="uppercase" color={subColor} mb={1}>
                  {`${g.label} · ${g.members.length}`}
                </Text>
                {g.members.map((u) => (
                  <Flex
                    key={u.id}
                    as="button"
                    type="button"
                    w="full"
                    textAlign="left"
                    align="center"
                    px={2}
                    py={1.5}
                    borderRadius="md"
                    _hover={{ bg: rowHover }}
                    onClick={() => { modal.onClose(); startUserImpersonation(u.id, u.name); }}
                  >
                    <Box minW={0} flex="1">
                      <Text fontSize="sm" fontWeight="medium" noOfLines={1}>{u.name || '(unnamed)'}</Text>
                      {(u as any).email && <Text fontSize="xs" color={subColor} noOfLines={1}>{(u as any).email}</Text>}
                    </Box>
                    {u.isActive === false && (
                      <Badge ml={2} colorScheme="yellow" variant="subtle" flexShrink={0}>Invited</Badge>
                    )}
                  </Flex>
                ))}
              </Box>
            ))}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ImpersonateUserGroup;
